import { SessionTicketWidget } from "@components/film/SessionTicketWidget"
import { Button } from "@components/ui/Button"
import { Dialog } from "@components/ui/Dialog"
import { Spinner } from "@components/ui/Spinner"
import { SessionTicketsFragment } from "@generated-types"
import { useBreakpoint } from "@hooks/useBreakpoint"
import { ReactNode, useEffect } from "react"
import { proxy, useSnapshot } from "valtio"
import { useFerveEmbed } from "./useFerveEmbed"
import { useFlyingCarpet } from "@features/flying-carpet/useFlyingCarpet"
import { Drawer } from "@components/ui/Drawer"

type TicketModalItem =
  | {
      type: "embed"
      title?: string
      itemHash: string
    }
  | {
      type: "sessions"
      title?: string
      sessions?: SessionTicketsFragment[]
    }

export const ticketModals = proxy({
  stack: [] as TicketModalItem[],
  push(item: TicketModalItem) {
    ticketModals.stack.push(item)
  },
  pop() {
    ticketModals.stack.pop()
  },
  closeIndex(index: number) {
    ticketModals.stack.splice(index, 1)
  },
  closeAll() {
    ticketModals.stack = []
  },
})

export function TicketModalSystem() {
  const carpet = useFlyingCarpet()
  const stack = useSnapshot(ticketModals)

  useEffect(() => {
    if (carpet.isOpen) {
      ticketModals.closeAll()
    }
  }, [carpet.isOpen])

  if (!env.client) return <></>

  return (
    <>
      {stack.stack.map((item, index) => (
        <TicketModalItem key={index} item={item as TicketModalItem} close={() => stack.closeIndex(index)} />
      ))}
    </>
  )
}

function TicketModalItem(props: { item: TicketModalItem; close: () => void }) {
  let contents: ReactNode = null

  const isMobile = useBreakpoint().match(/BASE|sm/)

  if (props.item.type === "sessions") {
    contents = (
      <div className="flex flex-col gap-4 p-4">
        {props.item.sessions?.map((session) => {
          if (!session) return null
          return <SessionTicketWidget key={session.id} session={session} alwaysOpenTicketsInModal />
        })}
      </div>
    )
  } else if (props.item.type === "embed") {
    contents = <ModalFerveEmbed itemHash={props.item.itemHash} close={props.close} />
  } else {
    return null
  }

  if (isMobile) {
    return (
      <Drawer
        open
        onAnimationEnd={(open) => {
          if (!open) {
            props.close()
          }
        }}
        padded={false}
        repositionInputs={false}
        className="theme-light"
      >
        <div className="flex md:max-w-[700px] md:w-[80vw] justify-between items-center gap-3 p-4 flex-none">
          <Drawer.Title className="flex-1 type-title-m">{props.item.title ?? "Make your selection below"}</Drawer.Title>
        </div>
        <div className="overflow-auto h-full max-h-[calc(100svh-120px)] flex-1 border-t border-bg-mid">{contents}</div>
      </Drawer>
    )
  } else {
    return (
      <Dialog
        open
        onOpenChange={(open) => {
          if (!open) {
            props.close()
          }
        }}
        className="p-0 theme-light"
      >
        <div className="flex md:max-w-[700px] md:w-[80vw] justify-between items-center gap-3 p-4 flex-none">
          <Dialog.Title className="flex-1 type-title-m">{props.item.title ?? "Make your selection below"}</Dialog.Title>
          <Button onClick={props.close} icon="cross" shape="icon" variant="ghost" className="flex-none" />
        </div>
        <div className="overflow-auto h-full flex-1 border-t border-bg-mid">{contents}</div>
      </Dialog>
    )
  }
}

function ModalFerveEmbed(props: { itemHash: string; close: () => void }) {
  const embed = useFerveEmbed({
    itemHash: props.itemHash,
  })

  useEffect(() => {
    embed.open()
  }, [])

  return (
    <div>
      {embed.isLoading && (
        <div key="spinner" className="flex items-center justify-center p-6">
          <Spinner />
        </div>
      )}
      <div key="embed" className={embed.isLoading ? "opacity-0 h-0 absolute" : ""}>
        {embed.child}
      </div>
    </div>
  )
}
